import React from "react";
import cx from "classnames";
import { Button } from "@picklebet/ui";
import { useIsLoggedIn } from "@/features/auth/hooks/useIsLoggedIn";
import { useSignIn } from "@/features/auth/hooks/useSignIn";
import { navigate } from "utilities/navigate";
import TrashIconButton from "./TrashIconButton";
import type { PickemsContestWithMarkets } from "@/hooks/firestore/pickems/usePickemsContest";
import type { ProcessedEntry } from "@/hooks/firestore/betting/useEntries";
import { usePickslipStore } from "@/features/pickems/stores/pickslipStore";
import { useShellStore } from "@/features/shell/stores/shellStore";
import { submitPicks } from "@/features/pickems/services/submitPicks";
import { ChevronDownIcon } from "@picklebet/ui";
import { triggerHaptic } from "@/utilities/haptics";

import * as styles from "./PickSlip.module.scss";

type PickSlipFooterActionProps = {
  contest: PickemsContestWithMarkets;
  entry?: ProcessedEntry;
  hasMadeAllPicks: boolean;
  canMakePick: boolean;
};

const PickSlipFooterAction = ({
  contest,
  entry,
  hasMadeAllPicks,
  canMakePick,
}: PickSlipFooterActionProps) => {
  const isLoggedIn = useIsLoggedIn();
  const confirmContestId = usePickslipStore((state) => state.confirmContestId);
  const isLoading = usePickslipStore((state) => state.isLoading);
  const pickAcceptedId = usePickslipStore((state) => state.pickAcceptedId);
  const updatePickslip = usePickslipStore((state) => state.updatePickslip);
  const reset = usePickslipStore((state) => state.reset);
  const closeDock = useShellStore((state) => state.closeDock);
  const { handleSignIn, isBiometricProcessing } = useSignIn();
  const picksAccepted = !!pickAcceptedId;

  const handleClearAllSelections = () => {
    reset();
    triggerHaptic.error();
  };

  if (!isLoggedIn) {
    return (
      <>
        <TrashIconButton onClick={handleClearAllSelections} />
        <Button
          className={styles.button}
          size={`md`}
          loading={isBiometricProcessing}
          onClick={handleSignIn}
        >
          Log In To Pick
        </Button>
      </>
    );
  }

  if (confirmContestId && contest && confirmContestId === contest.id) {
    const buttonText = picksAccepted
      ? "View Lobby"
      : hasMadeAllPicks
        ? "Confirm Changes"
        : "Confirm Picks";

    return (
      <>
        {picksAccepted ? (
          <TrashIconButton onClick={handleClearAllSelections} />
        ) : (
          <Button
            variant={`blurred`}
            className={cx(styles.iconButton, styles.back)}
            onClick={() => {
              updatePickslip({ confirmContestId: null });
              triggerHaptic.click();
            }}
          >
            <ChevronDownIcon />
          </Button>
        )}
        <Button
          className={styles.button}
          variant={picksAccepted ? "primary" : "warning"}
          size={`md`}
          onClick={() => {
            if (picksAccepted) {
              closeDock();
              navigate("/pickems/");
            } else {
              submitPicks(contest, entry);
            }
            triggerHaptic.click();
          }}
          disabled={isLoading}
          loading={isLoading}
        >
          {buttonText}
        </Button>
      </>
    );
  }

  return (
    <>
      <TrashIconButton onClick={handleClearAllSelections} />
      <Button
        className={styles.button}
        size={`md`}
        disabled={!canMakePick || isLoading}
        loading={isLoading}
        onClick={() => {
          submitPicks(contest, entry);
          triggerHaptic.click();
        }}
      >
        {hasMadeAllPicks ? "Make Changes" : "Submit Picks"}
      </Button>
    </>
  );
};

export default PickSlipFooterAction;
